import type { MouseEventHandler } from 'react';
import React, { useCallback, useEffect, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { Box } from '@atlaskit/primitives/compiled/box';
import { updatePageRovoParams } from '@atlaskit/rovo-triggers';

import { preloadChatOnIntent } from '@atlassian/conversation-assistant/preload-chat-on-intent';
import { usePrefetchRecommendedSpacesOnOpenIntent } from '@atlassian/conversation-assistant/recommended-spaces-open-intent';
import { ConversationAssistantButtonNav4 } from '@atlassian/conversation-assistant-ui-components';
import { ChatContextSubscriber } from '@atlassian/conversation-assistant-store';
import {
	CONVERSATION_ASSISTANT_BRAND_NAME,
	ChatOpenerSubscriber,
	ConversationAssistantUIModes,
} from '@atlassian/conversation-assistant';

import { GeneralShortcutListener } from '@confluence/shortcuts/entry-points/GeneralShortcutListener.ts';
import {
	isMac,
	useShortcutStrings,
} from '@confluence/shortcuts/entry-points/ShortcutVisualizer.ts';
import {
	AI_MATE_SHORTCUT,
	UNIVERSAL_AI_MATE_SHORTCUT_MAC,
	UNIVERSAL_AI_MATE_SHORTCUT_WIN,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useIsEditorOpen } from '@confluence/page-utils/entry-points/useIsEditorOpen.ts';
import { fg } from '@confluence/feature-gating';

import { useToggleConversationAssistant } from './hooks/useToggleConversationAssistant.ts';

export const ConversationAssistantNavBarButton = ({
	shouldRenderControllers = true,
}: {
	shouldRenderControllers?: boolean;
} = {}): React.JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { cloudId } = useSessionData();
	const isEditorOpen = useIsEditorOpen();

	const { mode, toggleConversationAssistant, isConversationAssistantSidebarActive } =
		useToggleConversationAssistant();
	const buttonRef = useRef<HTMLButtonElement>(null);
	const shouldRestoreFocusRef = useRef(false);
	const wasChatOpenRef = useRef(false);
	const isChatOpen =
		isConversationAssistantSidebarActive || mode === ConversationAssistantUIModes.MINI_MODAL;

	useEffect(() => {
		if (wasChatOpenRef.current && !isChatOpen) {
			// A sidebar replacement can hide chat without closing its UI mode.
			// Only a completed close should return focus to the opening button.
			if (
				shouldRestoreFocusRef.current &&
				mode === undefined &&
				// eslint-disable-next-line confluence-feature-gating/no-new-feature-gates -- Existing gate retained during CCP-9677 lint-rule rollout; migration deferred.
				fg('confluence_rovo_nav_button_restore_focus')
			) {
				buttonRef.current?.focus({ preventScroll: true });
			}
			shouldRestoreFocusRef.current = false;
		}
		wasChatOpenRef.current = isChatOpen;
	}, [isChatOpen, mode]);

	const prefetchRecommendedSpacesOnOpenIntent = usePrefetchRecommendedSpacesOnOpenIntent({
		siteId: cloudId,
	});

	const prefetchRecommendedSpacesIfChatWillOpen = useCallback(() => {
		if (isConversationAssistantSidebarActive || mode === ConversationAssistantUIModes.MINI_MODAL) {
			return;
		}

		prefetchRecommendedSpacesOnOpenIntent();
	}, [isConversationAssistantSidebarActive, mode, prefetchRecommendedSpacesOnOpenIntent]);

	const handleOnClick = useCallback<MouseEventHandler>(
		(e) => {
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'navigationItem',
					actionSubjectId: 'aiMate',
					source: 'AIMate',
					attributes: {
						opened: !isConversationAssistantSidebarActive,
						navigationLayer: 'global',
						navigationContainer: 'top',
						navVersion: '4',
					},
					tags: ['atlaskit', 'aiMate'],
				},
			}).fire();
			e.preventDefault();
			if (!isChatOpen) {
				shouldRestoreFocusRef.current = true;
			}
			prefetchRecommendedSpacesIfChatWillOpen();
			toggleConversationAssistant();
		},
		[
			createAnalyticsEvent,
			isChatOpen,
			isConversationAssistantSidebarActive,
			prefetchRecommendedSpacesIfChatWillOpen,
			toggleConversationAssistant,
		],
	);

	const handleMouseEnter = useCallback<MouseEventHandler>(() => {
		// Intent-time preload — FG-gated, once-only per page load, so safe on every hover.
		if (!isConversationAssistantSidebarActive) {
			preloadChatOnIntent();
		}
	}, [isConversationAssistantSidebarActive]);

	const universalAIMateShortcut = isMac()
		? UNIVERSAL_AI_MATE_SHORTCUT_MAC
		: UNIVERSAL_AI_MATE_SHORTCUT_WIN;

	const shortcutStrings = useShortcutStrings({ shortcut: universalAIMateShortcut });

	return (
		<Box as="span" paddingInlineStart="space.050" testId="app-navigation-ai-mate">
			<ConversationAssistantButtonNav4
				ref={buttonRef}
				siteId={cloudId}
				productKey="confluence"
				enableOnboardingSpotlight
				onClick={handleOnClick}
				onMouseEnter={handleMouseEnter}
				isActive={isConversationAssistantSidebarActive}
				tooltipContent={CONVERSATION_ASSISTANT_BRAND_NAME}
				shortcut={
					// No shortcut on edit pages
					!isEditorOpen ? shortcutStrings : undefined
				}
			/>
			{shouldRenderControllers && (
				<>
					<ChatContextSubscriber />
					<ChatOpenerSubscriber
						extensionContextBridgeEnabled
						onTriggerOpeningChat={(conversationId, openChatMode, _source, autoFocusInput) => {
							if (
								isConversationAssistantSidebarActive ||
								mode === ConversationAssistantUIModes.MINI_MODAL
							) {
								return;
							}

							conversationId &&
								updatePageRovoParams({
									pathway: 'chat',
									conversationId,
								});

							prefetchRecommendedSpacesOnOpenIntent();
							toggleConversationAssistant(openChatMode, autoFocusInput);
						}}
					/>
					<GeneralShortcutListener
						deprecatedAccelerator={!isEditorOpen ? AI_MATE_SHORTCUT : undefined}
						accelerator={universalAIMateShortcut}
						listener={() => {
							prefetchRecommendedSpacesIfChatWillOpen();
							toggleConversationAssistant('sidebar');
						}}
						isAppNavigationShortcut
					/>
				</>
			)}
		</Box>
	);
};
